import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useJWTAuth } from '../../contexts/useJWTAuth';
import { useAppContext } from '../../contexts/useAppContext';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { dashboardService } from '../../services/DashboardService';
import { analytics } from '../../services/analytics/posthog';
import { logger } from '../../utils/logger';
import type { DashboardData, ConnectedRepository } from '../../types/dashboard';
import type { User } from '../../contexts/jwtAuthContextInstance';

const ITEMS_PER_PAGE = 20;

export function useDashboardData(propUser?: User | null, isFirstVisit = false) {
  const { user: jwtUser } = useJWTAuth();
  const user = propUser || jwtUser;
  const { isDemoMode, flowType, demoState, setDemoMode } = useAppContext();
  const { data: subscriptionStatus } = useSubscriptionStatus();

  const [loading, setLoading] = useState(true);
  // Tracks pagination/filter/sort refetches separately from the initial load,
  // so the list keeps showing the previous page instead of flashing back to
  // the skeleton on every chip click. `hasLoadedRef` flips on the first
  // successful fetch and stays true — using repositories.length here would
  // re-trigger the skeleton whenever a filter returns 0 rows.
  const [isRefetching, setIsRefetching] = useState(false);
  const hasLoadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData>(
    dashboardService.createMockDashboardData(user, isFirstVisit),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;
  const sortBy = searchParams.get('sort') || 'lastAnalysis';
  const sortOrder = (searchParams.get('order') as 'asc' | 'desc') || 'desc';
  const filterStatus = searchParams.get('status') || 'all';

  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const resolved = typeof page === 'function' ? page(currentPage) : page;
      if (resolved <= 1) next.delete('page');
      else next.set('page', String(resolved));
      return next;
    });
  };
  const setSortBy = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'lastAnalysis') next.delete('sort');
      else next.set('sort', value);
      next.delete('page');
      return next;
    });
  };
  const setSortOrder = (value: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const resolved = typeof value === 'function' ? value(sortOrder) : value;
      if (resolved === 'desc') next.delete('order');
      else next.set('order', resolved);
      return next;
    });
  };
  const setFilterStatus = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('status');
      else next.set('status', value);
      next.delete('page');
      return next;
    });
  };

  // `repositories` is the current page from the server. The "all" footprint
  // is no longer materialized in the browser — the server applies sort/filter/page
  // and returns just the slice the UI renders. `counts` powers the chip group
  // ("All N / Issues N …") across the whole account, not just the visible page.
  const [repositories, setRepositories] = useState<ConnectedRepository[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    analyzing: 0,
    error: 0,
    issues: 0,
    healthy: 0,
  });

  useEffect(() => {
    if (user?.id && (isDemoMode || flowType === 'demo')) {
      setDemoMode(false);
    }
  }, [user?.id, isDemoMode, flowType, setDemoMode]);

  useEffect(() => {
    const loadDashboardData = async () => {
      const firstLoad = !hasLoadedRef.current;
      try {
        if (firstLoad) setLoading(true);
        else setIsRefetching(true);
        setError(null);

        if (isDemoMode || flowType === 'demo' || demoState.preventNetworkRequests) {
          setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
          return;
        }

        if (user?.id) {
          try {
            const [data, page] = await Promise.all([
              dashboardService.getDashboardData(),
              dashboardService.getAnalyzedRepositories({
                page: currentPage,
                perPage: ITEMS_PER_PAGE,
                status: filterStatus,
                sortBy,
                sortOrder,
              }),
            ]);
            setRepositories(page.data);
            setTotalCount(page.meta.total);
            setStatusCounts(page.counts);
            setDashboardData({
              ...data,
              repositories: page.data,
            });
          } catch (apiError) {
            logger.warn('[Dashboard] API fetch failed, using mock data', { error: apiError });
            setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
          }
        } else {
          setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
        }
      } catch (err) {
        logger.error('Failed to load dashboard data', { error: err });
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
        setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
        setIsRefetching(false);
      }
    };

    loadDashboardData();
    // user identity is captured via user?.id; full user object is read inside the effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    isFirstVisit,
    isDemoMode,
    flowType,
    demoState.preventNetworkRequests,
    currentPage,
    sortBy,
    sortOrder,
    filterStatus,
  ]);

  useEffect(() => {
    analytics.pageViewed('dashboard');
  }, []);

  const totalDashboardPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  const refreshDashboardData = async () => {
    setLoading(true);
    setError(null);
    analytics.dashboardInteraction({ action: 'refresh_clicked' });

    try {
      if (isDemoMode || flowType === 'demo' || demoState.preventNetworkRequests) {
        setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
        setLoading(false);
        return;
      }

      if (user?.id) {
        try {
          const [data, page] = await Promise.all([
            dashboardService.getDashboardData(),
            dashboardService.getAnalyzedRepositories({
              page: currentPage,
              perPage: ITEMS_PER_PAGE,
              status: filterStatus,
              sortBy,
              sortOrder,
            }),
          ]);
          setRepositories(page.data);
          setTotalCount(page.meta.total);
          setDashboardData({
            ...data,
            repositories: page.data,
          });
        } catch (apiError) {
          logger.warn('[Dashboard] API refresh failed, using mock data', { error: apiError });
          setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
        }
      } else {
        setDashboardData(dashboardService.createMockDashboardData(user, isFirstVisit));
      }
    } catch (refreshError) {
      logger.error('Failed to refresh dashboard data', { error: refreshError });
      setError(refreshError instanceof Error ? refreshError.message : 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    isDemoMode,
    flowType,
    subscriptionStatus,
    loading,
    isRefetching,
    error,
    setError,
    dashboardData,
    setDashboardData,
    repositories,
    totalCount,
    statusCounts,
    totalDashboardPages,
    currentPage,
    setCurrentPage,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterStatus,
    setFilterStatus,
    refreshDashboardData,
    ITEMS_PER_PAGE,
  };
}
